import { z } from 'zod'
import { salesItemKindSchema } from '../enums/sales-item-kind.enum.js'
import { MAX_MILLI } from '../helpers/sales-money.js'
import { calendarDaySchema } from '../validators/temporal.validator.js'

/**
 * A venue's price list (docs/sales.spec.md, "The price list"): its tax and
 * service charge, its seasons, and what it sells, venue-wide or on one track.
 * Money is integer cents; rates are thousandths of a percent (8.875% is 8875).
 */

export const salesRatesSchema = z.object({
  taxMilli: z.number().int().min(0).max(MAX_MILLI),
  serviceChargeMilli: z.number().int().min(0).max(MAX_MILLI),
  /** Whether tax is charged on the service charge too. */
  serviceChargeTaxed: z.boolean(),
})

export const salesSeasonDtoSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  startsOn: calendarDaySchema,
  /** Inclusive. */
  endsOn: calendarDaySchema,
})

export const salesPackagePriceDtoSchema = z.object({
  /** Null: any date outside the seasons. */
  seasonId: z.string().uuid().nullable(),
  /** 0 is Sunday, 6 Saturday. */
  weekdays: z.array(z.number().int().min(0).max(6)),
  priceCents: z.number().int().nonnegative(),
})

export const salesItemDtoSchema = z.object({
  id: z.string().uuid(),
  kind: salesItemKindSchema,
  /** Null: offered on every track. */
  trackId: z.string().uuid().nullable(),
  name: z.string(),
  description: z.string(),
  /** An add-on's price, or the price per guest; null for a package, priced by `prices`. */
  priceCents: z.number().int().nonnegative().nullable(),
  taxable: z.boolean(),
  serviceCharged: z.boolean(),
  prices: z.array(salesPackagePriceDtoSchema),
})
export type SalesItemDto = z.infer<typeof salesItemDtoSchema>

export const salesPriceListDtoSchema = z.object({
  venueId: z.string().uuid(),
  rates: salesRatesSchema,
  seasons: z.array(salesSeasonDtoSchema),
  /** In the venue's order. */
  items: z.array(salesItemDtoSchema),
  /** The venue's tracks, for an item only one of them offers. */
  tracks: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
  /** Whether this person may change prices, tax and service charge. */
  canEdit: z.boolean(),
})
export type SalesPriceListDto = z.infer<typeof salesPriceListDtoSchema>
